import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  InjectionToken,
  OnDestroy
} from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';

import { getScriptUrl } from 'src/app/core/config/url.config';
import { CURRENT_HOST_ID } from 'src/app/core/host-identification/current-host-id';
import { HostId } from 'src/app/core/host-identification/host-id.enum';
import { OutputEmitter } from 'src/app/shared/price-display/output-emitter';
import { updateIsPlsInitialized } from 'src/app/store/common/common.actions';
import { ScriptParams } from 'src/app/utils/functions/load-external-script';

import { ScriptLoaderModule } from '../script-loader/script-loader.module';

import { PriceDisplayEventData, PriceDisplayParams, PriceDisplayStatus, ProductData } from './price-display.interface';
import { PriceDisplayStore } from './price-display.store';

export const OUTPUT_EMITTER_KEY = {
  status: 'status'
};

export const PRICE_DISPLAY_PARAMS = new InjectionToken<PriceDisplayParams>('PriceDisplayParams');
export const OUTPUT_EMITTER = new InjectionToken<OutputEmitter<PriceDisplayStatus>>('OutputEmitter');
export const CONTEXT = new InjectionToken<OutputEmitter<PriceDisplayStatus>>('OutputEmitter');

/**
 * Price display component.
 *
 * Documentation: https://pages.git.i.mercedes-benz.com/dh-io-pls/dcps-product-pricing/v3/components/pls-ui
 */

type ValidEnvironment = HostId.Dev | HostId.Int | HostId.PreProd | HostId.Prod | undefined;
@Component({
  selector: 'cc-price-display',
  templateUrl: './price-display.component.html',
  styleUrl: './price-display.component.scss',
  providers: [PriceDisplayStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ScriptLoaderModule, LetDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PriceDisplayComponent implements OnDestroy {
  public darkMode: boolean | undefined;
  public size: string | undefined;
  public isUsingUILabel = false;
  public isLabelCenteredOnMQ1 = false;

  public plsEnvironment: ValidEnvironment = this.getCurrentPlsEnvironment(CURRENT_HOST_ID);

  public readonly scriptParams: ScriptParams = { scriptUrl: getScriptUrl('priceDisplay'), isModule: true };
  public readonly footnoteModuleId!: string | undefined;

  private readonly hasScriptLoadedSubject = new BehaviorSubject<boolean>(false);

  public constructor(
    private readonly priceDisplayStore: PriceDisplayStore,
    private readonly store: Store,
    @Inject(PRICE_DISPLAY_PARAMS) private readonly priceDisplayParams: PriceDisplayParams,
    @Inject(OUTPUT_EMITTER) private readonly outputEmitter: OutputEmitter<PriceDisplayStatus>
  ) {
    this.footnoteModuleId = this.priceDisplayParams?.isWithinModal ? 'inModal' : undefined;
    this.isUsingUILabel = this.priceDisplayParams?.useUILabel || false;
    this.isLabelCenteredOnMQ1 = this.priceDisplayParams?.isLabelCenteredOnMQ1 || false;
    this.darkMode = this.priceDisplayParams?.darkMode;
    this.size = this.priceDisplayParams?.size;
    this.priceDisplayStore.updateParams(this.priceDisplayParams);
  }

  public get product$(): Observable<ProductData> {
    return combineLatest([this.priceDisplayStore.product$, this.hasScriptLoadedSubject.asObservable()]).pipe(
      filter(([, hasLoaded]) => !!hasLoaded),
      map(([product]) => product)
    );
  }

  public scriptLoaded(): void {
    this.hasScriptLoadedSubject.next(true);
  }

  public onPriceDisplayInitializeOrUpdate(event: CustomEvent<PriceDisplayEventData[]>): void {
    const eventData = event?.detail[0];
    this.outputEmitter.emit(OUTPUT_EMITTER_KEY.status, eventData?.plsStatus);
    this.priceDisplayStore.updatePlsStatus(eventData?.plsStatus);
    this.store.dispatch(updateIsPlsInitialized({ isPlsInitialized: true }));
  }

  public onPriceDisplayDisconnected(): void {
    this.outputEmitter.emit(OUTPUT_EMITTER_KEY.status, PriceDisplayStatus.Unmount);
    this.priceDisplayStore.updatePlsStatus(PriceDisplayStatus.Unmount);
    this.store.dispatch(updateIsPlsInitialized({ isPlsInitialized: false }));
  }

  public ngOnDestroy(): void {
    this.outputEmitter.emit(OUTPUT_EMITTER_KEY.status, PriceDisplayStatus.Unmount);
  }

  private getCurrentPlsEnvironment(hostId: HostId): ValidEnvironment {
    return (
      {
        [HostId.Local]: HostId.Dev,
        [HostId.Dev]: HostId.Dev,
        [HostId.Int]: HostId.Int,
        [HostId.PreProd]: HostId.PreProd
      }[hostId as ValidEnvironment & HostId.Local] || undefined
    );
  }
}
